import type { TableSortValue } from "@src/shared/constants/sort-order.constants";
import apiService from "./api";
import { sanitizeQueryParams } from "@src/utility/helpers/apiHelper";
import {
  USER_GUIDE_STATUS,
  type UserGuideStatusType,
  USER_GUIDE_TYPE,
  type UserGuideContentType,
} from "@src/views/apps/user-guide/constants";

export {
  USER_GUIDE_STATUS,
  type UserGuideStatusType,
  type UserGuideStatusType as UserGuideStatus,
  USER_GUIDE_TYPE,
  type UserGuideContentType,
  type UserGuideContentType as UserGuideType,
};

// ============================================================================
// User Guide Category Interfaces & Mappers
// ============================================================================

export interface UserGuideCategoryListItem {
  rowId: number;
  id: number;
  title: string;
  description: string | null;
  status: UserGuideStatusType;
  createdAt: string;
  updatedAt: string;
}

export interface UserGuideCategoryOption {
  id: number;
  title: string;
  status: UserGuideStatusType;
}

export interface CreateUserGuideCategoryPayload {
  title: string;
  description?: string | null;
  status?: UserGuideStatusType;
}

export interface UpdateUserGuideCategoryPayload {
  title?: string;
  description?: string | null;
  status?: UserGuideStatusType;
}

export interface GetUserGuideCategoriesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: UserGuideStatusType;
  orderBy?: string;
  orderDirection?: TableSortValue;
}

export interface RawUserGuideCategory {
  rowId?: number;
  id?: number;
  title?: string;
  description?: string | null;
  status?: UserGuideStatusType;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// User Guide Interfaces & Mappers
// ============================================================================

export interface UserGuideListItem {
  rowId: number;
  id: number;
  categoryId: number;
  categoryTitle?: string;
  title: string;
  type: UserGuideContentType;
  body: string | null;
  fileUrl: string | null;
  status: UserGuideStatusType;
  createdAt: string;
  updatedAt: string;
}

export interface UserGuideDetail {
  id: number;
  categoryId: number;
  categoryTitle?: string;
  title: string;
  type: UserGuideContentType;
  body: string | null;
  fileUrl: string | null;
  status: UserGuideStatusType;
  createdAt: string;
  updatedAt: string;
}

export interface CreateUserGuidePayload {
  categoryId: number;
  title: string;
  type: UserGuideContentType;
  body?: string | null;
  fileUrl?: string | null;
  status?: UserGuideStatusType;
}

export interface UpdateUserGuidePayload {
  categoryId?: number;
  title?: string;
  type?: UserGuideContentType;
  body?: string | null;
  fileUrl?: string | null;
  status?: UserGuideStatusType;
}

export interface GetUserGuidesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: UserGuideStatusType;
  type?: UserGuideContentType | string;
  categoryId?: number;
  orderBy?: string;
  orderDirection?: TableSortValue;
}

export interface RawUserGuide {
  rowId?: number;
  id?: number;
  categoryId?: number;
  categoryTitle?: string;
  title?: string;
  type?: UserGuideContentType;
  body?: string | null;
  fileUrl?: string | null;
  status?: UserGuideStatusType;
  createdAt?: string;
  updatedAt?: string;
}

// ============================================================================
// User Guide Category Service
// ============================================================================

export class UserGuideCategoryService {
  async getCategories(query?: GetUserGuideCategoriesParams) {
    return apiService.get("/user-guide-categories", {
      params: sanitizeQueryParams(query || {}),
    });
  }

  async getAllCategories(status?: UserGuideStatusType) {
    return apiService.get("/user-guide-categories/all", {
      params: sanitizeQueryParams({ status }),
    });
  }

  async getCategoryById(id: string | number) {
    return apiService.get(`/user-guide-categories/${id}`);
  }

  async createCategory(data: CreateUserGuideCategoryPayload) {
    return apiService.post("/user-guide-categories", data);
  }

  async updateCategory(id: string | number, data: UpdateUserGuideCategoryPayload) {
    return apiService.put(`/user-guide-categories/${id}`, data);
  }

  async deleteCategory(id: string | number) {
    return apiService.delete(`/user-guide-categories/${id}`);
  }
}

// ============================================================================
// User Guide Service
// ============================================================================

export class UserGuideService {
  async getUserGuides(query?: GetUserGuidesParams) {
    return apiService.get("/user-guides", {
      params: sanitizeQueryParams(query || {}),
    });
  }

  async getUserGuideById(id: string | number) {
    return apiService.get(`/user-guides/${id}`);
  }

  async createUserGuide(data: CreateUserGuidePayload) {
    return apiService.post("/user-guides", data);
  }

  async updateUserGuide(id: string | number, data: UpdateUserGuidePayload) {
    return apiService.put(`/user-guides/${id}`, data);
  }

  async deleteUserGuide(id: string | number) {
    return apiService.delete(`/user-guides/${id}`);
  }
}

export const userGuideCategoryService = new UserGuideCategoryService();
export const userGuideService = new UserGuideService();
